import React from 'react'

import { AnimatePresence, motion, usePresence } from 'framer-motion'
import { CloseIcon } from '@chakra-ui/icons'
import {
  Avatar,
  Box,
  Button,
  Heading,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useIsMutating } from '@tanstack/react-query'

import { TeamMember, TeamRole } from '@pkg/types/team'

import { availableRoles, isAdminRole } from '@/helpers/team'
import { useIsAdmin, useLeaveTeam } from '@/react-query/team'
import {
  useAllSentTeamInvites,
  useCancelTeamInvite,
  useUpdateTeamInviteRole,
} from '@/react-query/team-invites'
import {
  useTeamMembers,
  useUpdateTeamMemberRole,
} from '@/react-query/team-members'
import { useUser } from '@/react-query/user'
import { ArrowSvg } from '@/views/assets/ArrowSvg'
import { LinkedinAvatarFallback } from '@/views/assets/LinkedinAvatarFallback'
import { PlusIcon } from '@/views/assets/PlusIcon'
import { LeaveTeamModal } from '@/views/screens/Team/components/LeaveTeamModal'
import { RemoveFromTeamModal } from '@/views/screens/Team/components/RemoveFromTeamModal'
import { roleToLabel } from '@/views/screens/Team/const'

import { InviteTeamMemberModal } from './components/InviteTeamMemberModal'

export const TeamMembers = () => {
  const { onOpen, isOpen, onClose } = useDisclosure()

  const { data: teamMembers } = useTeamMembers()
  const isAdmin = useIsAdmin()

  const hasAts = teamMembers?.some((m) => m.isLinkedToAts !== undefined)

  return (
    <Box mt={20}>
      <HStack
        alignItems={'center'}
        flexWrap={'wrap'}
        spacing={{
          base: 4,
          md: 8,
        }}
      >
        <Heading fontSize="2xl" fontWeight="bold">
          Teammates
        </Heading>
        {isAdmin && (
          <Button
            variant="brand-solid"
            leftIcon={<PlusIcon stroke={'white'} mr={1} />}
            onClick={onOpen}
            flexShrink={0}
          >
            invite new teammate
          </Button>
        )}
      </HStack>

      <Box alignItems="center" mt={8}>
        <HStack
          alignItems={'center'}
          columnGap={8}
          display={{
            base: 'none',
            md: 'flex',
          }}
          key={'grid-heading'}
        >
          <NameGridHeading />
          <EmailGridHeading />
          <RoleGridHeading />
          {hasAts && isAdmin && <AtsGridHeading />}
        </HStack>

        <VStack mt={4} spacing={8} key={'team-members'}>
          <AnimatePresence>
            {teamMembers?.map((teamMember) => (
              <TeamMemberDisplay
                key={teamMember.email}
                teamMember={teamMember}
              />
            ))}
            {isAdmin && <SentTeamInvites />}
          </AnimatePresence>
        </VStack>
      </Box>

      <InviteTeamMemberModal isOpen={isOpen} onClose={onClose} />
    </Box>
  )
}

const EmailGridHeading: React.FC = () => {
  return (
    <Box display={'flex'} alignItems={'center'} w={'230px'} flexShrink={0}>
      <Text fontWeight="bold">Email</Text>
    </Box>
  )
}

const NameGridHeading: React.FC = () => {
  return (
    <Box display={'flex'} alignItems={'center'} w={'230px'} flexShrink={0}>
      <Box w={'2.5rem'} h={'2.5rem'} />
      <Text fontWeight="bold" ml={3}>
        Name
      </Text>
    </Box>
  )
}

const RoleGridHeading: React.FC = () => {
  return (
    <Box
      display={'flex'}
      alignItems={'center'}
      w={'250px'}
      pl={'16px'}
      flexShrink={0}
    >
      <Text fontWeight="bold">Role</Text>
    </Box>
  )
}

const AtsGridHeading: React.FC = () => {
  return (
    <Box display={'flex'} alignItems={'center'} w={'140px'} flexShrink={0}>
      <Text fontWeight="bold">ATS</Text>
    </Box>
  )
}

type TeamMemberDisplayProps = React.ComponentPropsWithRef<typeof Stack> & {
  teamMember: TeamMember
}

const TeamMemberDisplay: React.FC<TeamMemberDisplayProps> = ({
  teamMember,
  ref,
}) => {
  const [isPresent, safeToRemove] = usePresence()

  const { data: user } = useUser()

  const leaveTeam = useLeaveTeam()

  const isSelf = teamMember.email === user?.email

  const isOnlyAdmin =
    (useTeamMembers()?.data || []).filter((member) => isAdminRole(member.role))
      .length === 1

  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isRemoveOpen,
    onOpen: onRemoveOpen,
    onClose: onRemoveClose,
  } = useDisclosure()

  const isAdmin = useIsAdmin()

  const updateTeamMemberRole = useUpdateTeamMemberRole()

  const isUpdatingTeamMemberRole = useIsMutating({
    mutationKey: ['updateTeamMemberRole'],
  })

  return (
    <Stack
      direction={{
        base: 'column',
        lg: 'row',
      }}
      as={motion.div}
      ref={ref}
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: {
          ease: 'linear',
        },
      }}
      alignItems={{
        base: 'flex-start',
        lg: 'center',
      }}
      w={'100%'}
      columnGap={8}
      rowGap={4}
      onAnimationComplete={() => !isPresent && safeToRemove()}
      flexWrap={'wrap'}
    >
      <Box display={'flex'} alignItems={'center'} w={'230px'} flexShrink={0}>
        <Avatar
          src={teamMember.photoURL}
          w={'2.5rem'}
          h={'2.5rem'}
          icon={
            <LinkedinAvatarFallback
              h={'2.5rem'}
              w={'2.5rem'}
              rounded={'full'}
            />
          }
        />
        <Text ml={3} fontWeight={'500'}>
          {teamMember.displayName}
        </Text>
      </Box>
      <Text fontWeight={'500'} w={'230px'} flexShrink={0}>
        {teamMember.email}
      </Text>
      {isAdmin ? (
        <Menu>
          <MenuButton
            rounded={'1.25rem'}
            as={Button}
            rightIcon={<ArrowSvg ml={4} verticalAlign={'middle'} />}
            variant={'unstyled'}
            w={'250px'}
            px={4}
            bgColor={'white'}
            _hover={{
              backgroundColor: '#E0E0E0',
            }}
            textAlign={'left'}
            justifyContent={'space-between'}
            display={'flex'}
            fontWeight={'500'}
            alignItems={'center'}
            verticalAlign={'middle'}
            isDisabled={isUpdatingTeamMemberRole > 0}
          >
            {updateTeamMemberRole.isPending ? (
              <Spinner alignItems={'center'} verticalAlign={'middle'} />
            ) : (
              roleToLabel[teamMember.role]
            )}
          </MenuButton>
          <MenuList>
            {availableRoles
              ?.filter((role) => {
                // if the user is the only admin, they cannot change their role to non-admin.
                return !isOnlyAdmin || !isSelf || isAdminRole(role)
              })
              .map((availableRole) => (
                <MenuItem
                  key={availableRole}
                  onClick={async () => {
                    if (teamMember.role !== availableRole) {
                      await updateTeamMemberRole.mutateAsync({
                        email: teamMember.email,
                        newRole: availableRole,
                      })
                    }
                  }}
                  fontWeight={'normal'}
                  bgColor={
                    teamMember.role === availableRole ? '#f6f6f6' : 'white'
                  }
                >
                  {roleToLabel[availableRole]}
                </MenuItem>
              ))}
          </MenuList>
        </Menu>
      ) : (
        <Text fontWeight={'500'} w={'250px'} px={'16px'} flexShrink={0}>
          {roleToLabel[teamMember.role]}
        </Text>
      )}

      {isAdmin && teamMember.isLinkedToAts !== undefined && (
        <Box w={'140px'} flexShrink={0}>
          {teamMember.isLinkedToAts ? (
            <Text fontSize="sm" color="green.600" fontWeight="500">
              Linked
            </Text>
          ) : (
            <Tooltip
              label="This member's email doesn't match any employee in your ATS. Interview notes won't be attributed to them."
              placement="top"
              hasArrow
            >
              <Text
                fontSize="sm"
                color="orange.500"
                fontWeight="500"
                cursor="help"
              >
                Not linked
              </Text>
            </Tooltip>
          )}
        </Box>
      )}

      {isSelf ? (
        <Button
          variant={'brand-solid-red'}
          justifySelf={'flex-start'}
          onClick={() => {
            onOpen()
          }}
          isLoading={leaveTeam.isPending}
          h={'2.5rem'}
          flexShrink={0}
        >
          Leave the team
        </Button>
      ) : (
        isAdmin && (
          <Button
            variant={'brand-solid-gray'}
            justifySelf={'flex-start'}
            leftIcon={<CloseIcon mr={1} />}
            onClick={onRemoveOpen}
            h={'2.5rem'}
            flexShrink={0}
          >
            Remove from team
          </Button>
        )
      )}
      <LeaveTeamModal isOpen={isOpen} onClose={onClose} />
      <RemoveFromTeamModal
        isOpen={isRemoveOpen}
        onClose={onRemoveClose}
        teamMemberEmail={teamMember.email}
        teamMemberName={teamMember.displayName}
      />
    </Stack>
  )
}

export const SentTeamInvites: React.FC = () => {
  const sentTeamInvites = useAllSentTeamInvites().data

  return (
    <>
      {sentTeamInvites?.map((teamInvite) => (
        <TeamInviteDisplay
          key={teamInvite.id}
          id={teamInvite.id}
          email={teamInvite.email}
          role={teamInvite.role}
        />
      ))}
    </>
  )
}

type TeamInviteDisplayProps = React.ComponentPropsWithRef<typeof Stack> & {
  id: string
  email: string
  role: TeamRole
}

const TeamInviteDisplay: React.FC<TeamInviteDisplayProps> = ({
  id,
  email,
  role,
  ref,
}) => {
  const cancelTeamInvite = useCancelTeamInvite(id)

  const [isPresent, safeToRemove] = usePresence()

  const isAdmin = useIsAdmin()

  const updateTeamInviteRole = useUpdateTeamInviteRole()

  return (
    <Stack
      direction={{
        base: 'column',
        md: 'row',
      }}
      as={motion.div}
      ref={ref}
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: {
          ease: 'linear',
        },
      }}
      alignItems={{
        base: 'flex-start',
        md: 'center',
      }}
      w={'100%'}
      columnGap={8}
      rowGap={4}
      onAnimationComplete={() => !isPresent && safeToRemove()}
      flexWrap={'wrap'}
    >
      <Box display={'flex'} alignItems={'center'} w={'230px'} flexShrink={0}>
        <Avatar
          w={'2.5rem'}
          h={'2.5rem'}
          icon={
            <LinkedinAvatarFallback
              h={'2.5rem'}
              w={'2.5rem'}
              rounded={'full'}
            />
          }
        />
        <Text ml={3} fontWeight={'500'} color={'#7A7A7A'}>
          Invite Sent
        </Text>
      </Box>
      <Text color={'#7A7A7A'} fontWeight={'500'} w={'230px'} flexShrink={0}>
        {email}
      </Text>
      {isAdmin ? (
        <Menu>
          <MenuButton
            rounded={'1.25rem'}
            as={Button}
            rightIcon={<ArrowSvg ml={4} verticalAlign={'middle'} />}
            variant={'unstyled'}
            w={'250px'}
            px={4}
            bgColor={'white'}
            _hover={{
              backgroundColor: '#E0E0E0',
            }}
            textAlign={'left'}
            justifyContent={'space-between'}
            display={'flex'}
            fontWeight={'500'}
            alignItems={'center'}
            verticalAlign={'middle'}
          >
            {updateTeamInviteRole.isPending ? (
              <Spinner alignItems={'center'} verticalAlign={'middle'} />
            ) : (
              roleToLabel[role]
            )}
          </MenuButton>
          <MenuList>
            {availableRoles?.map((availableRole) => (
              <MenuItem
                key={availableRole}
                onClick={async () => {
                  if (role !== availableRole) {
                    await updateTeamInviteRole.mutateAsync({
                      teamInviteId: id,
                      role: availableRole,
                    })
                  }
                }}
                fontWeight={'normal'}
                bgColor={role === availableRole ? '#f6f6f6' : 'white'}
              >
                {roleToLabel[availableRole]}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      ) : (
        <Text fontWeight={'500'} w={'250px'} px={'16px'} flexShrink={0}>
          {roleToLabel[role]}
        </Text>
      )}
      <Button
        variant="brand-solid-gray"
        justifySelf={'flex-start'}
        leftIcon={<CloseIcon mr={1} />}
        onClick={() => cancelTeamInvite.mutate()}
        isLoading={cancelTeamInvite.isPending}
        h={'2.5rem'}
        flexShrink={0}
      >
        Revoke invitation
      </Button>
    </Stack>
  )
}
